/**
 * Attempts to track MobileDE events via Google Analytics(GA).
 * @param {*} selectedProduct The product selected
 * @param {*} selectedLeasingOffer The leasing offer selected
 * @param {*} isAuthenticated The user logged in
 * @param {*} customerType Private or Corporate
 * @param {*} deal_type BTO, checkout or classic
 * @param {*} commission Fixed values for each button
 * @param {*} category GA parameter pattern
 * @param {*} action GA parameter pattern
 * @param {*} checkout_step Checkout step definition
 * @param {*} label GA parameter pattern
 */

import { isMobile } from "react-device-detect";

import purgeNullValues from "utils/purgeNullValues";

import { getLeasingPrice } from "./getLeasingPrice";
import { parseAdAttributes } from "./parseAdAttributes";

/**
 * @returns {Object} - The tracking event object with null values purged from attributes property.
 */
const trackingMDE = ({
  selectedProduct,
  selectedLeasingOffer,
  isAuthenticated,
  customerType,
  deal_type = null,
  commission,
  category,
  action = undefined,
  checkout_step = undefined,
  label = undefined,
  ...rest
}) => ({
  event: "analytics_event",
  logged_in: isAuthenticated,
  attributes: purgeNullValues({
    customer_type: !isAuthenticated
      ? undefined
      : customerType?.customer?.type === "CORPORATE"
        ? "Commercial"
        : "Private",
    usage: selectedProduct?.leasingType,
    currency: selectedProduct?.handlingCosts?.currency,
    item_id: parseInt(selectedProduct?.id),
    checkout_step,
    availability: selectedProduct?.availability === "0" ? "in-stock" : selectedProduct?.availability,
    item_brand: selectedProduct?.brand,
    item_name: selectedProduct?.model,
    fuel_type: selectedProduct?.fuelType,
    contract_conditions: `mileage=${selectedLeasingOffer?.mileagePerYear};term=${selectedLeasingOffer?.leaseTerm}`,
    price: Math.round(parseFloat(selectedProduct?.grossBuyingPrice?.price)) || undefined,
    monthly_rate: getLeasingPrice(selectedLeasingOffer, customerType),
    item_category: "Car",
    item_subcategory: selectedProduct?.category,
    commission,
    lifestyle: "leasing",
    ad_attributes: parseAdAttributes(`
      dealType=${deal_type};
      configurator=${selectedProduct?.isCalculatorEnabled ? selectedProduct?.isNewConfiguratorEnabled ? "new" : "old" : undefined}
    `),
    fin_type: "null-leasing-offer",
    event_category: category,
    event_action: action,
    event_label: label,
    ...rest,
  }),
});

/**
 * Attempts to track MobileDE events via Google Analytics(GA).
 * @param {*} isAuthenticated The user logged in
 * @param {*} isProduction The application environment
 */

export const trackPageMeta = (
  isAuthenticated,
  isProduction = false,
  pageType = "VIP",
  hci,
  hui,
) => ({
  event: "pageMeta",
  hci,
  hui,
  logged_in: isAuthenticated,
  page_type: pageType,
  ga_property: isMobile ? "mweb" : "Desktop",
  staging: !isProduction,
  environment: isProduction ? "production" : "staging",
  selected_language: "DE",
});

export const trackPageView = ({
  customerType,
  pageName = "VIP",
  screenName = "VIP",
  price,
  dealType,
  availability,
  configurator,
  hsi,
  product,
  leasingOffer,
}) => ({
  event: "page_view",
  click_source_domain: document.referrer || undefined,
  page_type: pageName,
  screen_name: screenName,
  hsi,
  search_correlation_id: undefined,
  attributes: {
    item_category: "Car",
    item_subcategory: product?.category,
    item_brand: product?.brand,
    item_name: product?.model,
    item_id: parseInt(product?.id),
    price: Math.round(parseFloat(product?.grossBuyingPrice?.price)) || undefined,
    fin_type: "null-leasing-offer",
    fin_bank: product?.lessor.split(",")?.[0],
    item_condition: product?.isNewCar ? "New" : "Used",
    lifestyle: "leasing",
    ad_attributes: parseAdAttributes(`
      dealType=${dealType};
      configurator=${configurator}
    `),
    monthly_rate: price,
    usage: customerType || undefined,
    availability: product?.availability === "0" ? "in-stock" : product?.availability,
    contract_conditions: `mileage=${leasingOffer?.mileagePerYear};term=${leasingOffer?.leaseTerm}`,
    ad_type: undefined,
    seller_account_type: undefined,
    seller_review_count: undefined,
    seller_score: undefined,
    price_label: undefined,
  },
});

export default trackingMDE;
